import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { FlameIcon, LockIcon } from "lucide-react";
import { useLearner } from "../../contexts/LearnerContext";
import { useWords } from "../../contexts/WordsContext";
import { achievements } from "../../data/achievements";
import { useCountUp } from "../../hooks/useCountUp";
import { ProfileHeader } from "../../components/profile/ProfileHeader";
import { ActivityHeatmap } from "../../components/profile/ActivityHeatmap";
import { Card } from "../../components/ui/Card";
import { PairChips } from "../../components/ui/LangChip";
import { ProgressBar } from "../../components/ui/ProgressBar";
import { courseTitle, nextLevel } from "../../utils/language";
import { learnPath } from "../../utils/routes";
import { spring } from "../../utils/motion";
export function Profile() {
  const {
    totalXp,
    streak,
    courses
  } = useLearner();
  const {
    words
  } = useWords();
  const reduce = useReducedMotion() ?? false;
  const xp = useCountUp(totalXp, 1.4);
  const wordsLearned = courses.reduce((n, c) => n + c.wordsLearned, 0);
  const mastered = words.filter(w => w.status === "mastered").length;
  return <div className="mx-auto max-w-5xl px-4 py-6 md:px-8 md:py-10" data-id="element-1426" data-mp="1evaiesh3jum4">
      <ProfileHeader data-id="element-1427" data-mp="19vxus5ieijih" />

      <div className="mt-8 grid gap-6 lg:grid-cols-[1.3fr_1fr]" data-id="element-1428" data-mp="19vo4egtg2ac9">
        <section aria-labelledby="totals-title" className="tex-dots rounded-panel border-3 border-line bg-brand p-6 text-white shadow-brutal md:p-8" data-id="element-1429" data-mp="19yad4js2mvw2">
          <h2 id="totals-title" className="text-sm font-semibold text-white/85" data-id="element-1430" data-mp="i5tcqdi468kf">
            Total XP across {courses.length} {courses.length === 1 ? "course" : "courses"}
          </h2>
          <p className="mt-1 text-6xl font-bold tracking-[-0.03em] md:text-7xl" data-id="element-1431" data-mp="r56o5bpst77s">
            <span ref={xp.ref} data-id="element-1432" data-mp="27j6o2mon8wv1">{xp.value.toLocaleString()}</span>
          </p>
          <dl className="mt-8 grid grid-cols-3 gap-4 border-t-2 border-white/25 pt-5" data-id="element-1433" data-mp="27lj0kkbmuf0p">
            <div data-id="element-1434" data-mp="22mprd43dpa6c">
              <dt className="text-xs text-white/80" data-id="element-1435" data-mp="2alb26g714auk">Streak</dt>
              <dd className="mt-0.5 flex items-center gap-1 text-2xl font-bold" data-id="element-1436" data-mp="2al1eiz9gifs1">
                <FlameIcon className="h-5 w-5 text-accent" data-id="element-1437" data-mp="2ell8xv2nb5ah" /> {streak}
              </dd>
            </div>
            <div data-id="element-1438" data-mp="2jmir47nnnzbz">
              <dt className="text-xs text-white/80" data-id="element-1439" data-mp="2jjwkca8l9nut">Words learned</dt>
              <dd className="mt-0.5 text-2xl font-bold" data-id="element-1440" data-mp="2ojrbmewa0fge">{wordsLearned}</dd>
            </div>
            <div data-id="element-1441" data-mp="1qyd41e94py8z">
              <dt className="text-xs text-white/80" data-id="element-1442" data-mp="1qywq975tb8st">Saved & mastered</dt>
              <dd className="mt-0.5 text-2xl font-bold" data-id="element-1443" data-mp="1vvn0hduxra6b">{mastered}</dd>
            </div>
          </dl>
        </section>

        <Card as="section" labelledBy="levels-title" className="p-5" data-id="element-1444" data-mp="20twz5yfvcswo">
          <h2 id="levels-title" className="text-sm font-bold text-ink-2" data-id="element-1445" data-mp="25mhpgpzkgnvg">
            Levels by course
          </h2>
          <ul className="mt-4 space-y-4" data-id="element-1446" data-mp="18614t0drvd32">
            {courses.map(c => <li key={c.id} data-id="element-1447" data-mp="1d55dlpwsb8sv">
                <Link to={learnPath(c)} className="focus-ring flex items-center gap-3 rounded-xl" data-id="element-1448" data-mp="1d4cdv8w8tjk8">
                  <PairChips target={c.target} base={c.base} size="sm" data-id="element-1449" data-mp="15lw4zyw3re4v" />
                  <div className="min-w-0 flex-1 pl-1" data-id="element-1450" data-mp="10n1juqanxdhl">
                    <div className="flex items-baseline justify-between gap-2 text-sm" data-id="element-1451" data-mp="10lgudf37gkql">
                      <span className="truncate font-semibold text-ink" data-id="element-1452" data-mp="vqb7acy2h00x">{courseTitle(c)}</span>
                      <span className="shrink-0 font-bold text-ink" data-id="element-1453" data-mp="qr828si36tsy">
                        {c.level} → {nextLevel(c.level)}
                      </span>
                    </div>
                    <ProgressBar value={c.progress} size="sm" className="mt-1.5" label={`${courseTitle(c)} progress`} data-id="element-1454" data-mp="hv8ur6ypscf5" />
                  </div>
                </Link>
              </li>)}
            {courses.length === 0 && <li className="text-sm text-ink-2" data-id="element-1455" data-mp="7wjc3byzunxu">No courses yet.</li>}
          </ul>
        </Card>
      </div>

      <Card as="section" labelledBy="activity-title" className="mt-6 p-5" data-id="element-1456" data-mp="31dom8py117n">
        <h2 id="activity-title" className="mb-4 text-sm font-bold text-ink-2" data-id="element-1457" data-mp="3v455zx9k73dz">
          Practice calendar
        </h2>
        <ActivityHeatmap data-id="element-1458" data-mp="nqy4b7wqrzxi" />
      </Card>

      <section aria-labelledby="ach-title" className="mt-10" data-id="element-1459" data-mp="snybxn98paqq">
        <h2 id="ach-title" className="text-xl font-bold tracking-[-0.01em]" data-id="element-1460" data-mp="spj8fif473r8">
          Achievements
        </h2>
        <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-1461" data-mp="ixn5nu232pgo">
          {achievements.map((a, i) => {
          const Icon = a.icon;
          return <motion.li key={a.id} initial={reduce ? false : {
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-10% 0px"
          }} transition={{
            ...spring.smooth,
            delay: reduce ? 0 : i * 0.05
          }} className={`flex flex-col items-center rounded-card border-3 border-line p-4 text-center ${a.earned ? "bg-surface shadow-brutal-sm" : "border-dashed bg-transparent"}`} data-id="element-1462" data-mp="506yuhfvktoe" data-mpi={i}>
                <span className={`grid h-14 w-14 place-items-center rounded-full border-3 border-line ${a.earned ? "bg-accent text-night" : "bg-sunken text-ink-2"}`} data-id="element-1463" data-mp="3eukx7zkly9wp">
                  {a.earned ? <Icon className="h-6 w-6" strokeWidth={2.25} data-id="element-1464" data-mp="3omz5zca3sgsd" /> : <LockIcon className="h-5 w-5" data-id="element-1465" data-mp="3jtxkglfe5izt" />}
                </span>
                <p className="mt-3 text-sm font-bold text-ink" data-id="element-1466" data-mp="2do73fytnymv">{a.title}</p>
                <p className="mt-0.5 text-xs text-ink-2" data-id="element-1467" data-mp="2fsg2cbkvnu0">{a.description}</p>
              </motion.li>;
        })}
        </ul>
      </section>
    </div>;
}